import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import ReactMarkdown from 'react-markdown';
import {
  BookOpen, Megaphone, FilmStrip, Copy, Check, Download,
  CaretRight, ShareNetwork, Scales
} from '@phosphor-icons/react';

export default function MarketingKitPage() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);
  const [content, setContent] = useState('');
  const [copied, setCopied] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/library');
        setDocs(data);
        if (data.length) openDoc(data[0].id, data);
      } catch (err) {
        if (err.response?.status === 403) setContent('__forbidden__');
        console.error(err);
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDoc = async (id, list = docs) => {
    setCurrent(list.find(d => d.id === id));
    setCopied(false);
    try {
      const { data } = await api.get(`/library/${id}`);
      setContent(data.content);
    } catch {
      setContent('Failed to load.');
    }
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert('Copy failed — please select the text and copy manually.');
    }
  };

  const download = () => {
    const filename = (current?.title || 'document').replace(/[^\w]+/g, '-').toLowerCase() + '.md';
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    if (!current) return;
    setPdfBusy(true);
    try {
      const { data } = await api.get(`/library/${current.id}/pdf`, { responseType: 'blob' });
      const filename = (current.title || 'document').replace(/[^\w]+/g, '-').toLowerCase() + '.pdf';
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('PDF generation failed. Try again or use Copy as a fallback.');
    } finally {
      setPdfBusy(false);
    }
  };

  const printDoc = () => {
    // Open the doc in a new clean window for browser print-to-PDF or paper printing
    if (!current) return;
    const win = window.open('', '_blank', 'width=850,height=1100');
    if (!win) {
      alert('Pop-up blocked. Please allow pop-ups for this site, or use the PDF download button.');
      return;
    }
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${current.title}</title>
<style>
@media print { body { margin: 0; } }
body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif; font-size: 11pt; max-width: 7.5in; margin: 0.5in auto; line-height: 1.5; color: #222; }
h1 { color: #073763; border-bottom: 3px solid #F39C12; padding-bottom: 6px; }
h2, h3 { color: #0B5394; }
table { border-collapse: collapse; width: 100%; margin: 0.6em 0; }
th { background: #0B5394; color: white; padding: 6px 8px; text-align: left; }
td { border: 1px solid #ddd; padding: 5px 8px; }
tr:nth-child(even) td { background: #f8f9fa; }
blockquote { border-left: 3px solid #F39C12; padding-left: 12px; color: #555; font-style: italic; }
code { background: #f0f0f0; padding: 1px 4px; border-radius: 2px; }
hr { border: 0; border-top: 1px solid #ddd; }
</style></head><body id="print-body"></body></html>`);
    win.document.close();
    // Render markdown via the same library used in-app
    import('react-markdown').then(({ default: ReactMarkdown }) => {
      import('react-dom/client').then(({ createRoot }) => {
        const root = createRoot(win.document.getElementById('print-body'));
        root.render(<ReactMarkdown>{content}</ReactMarkdown>);
        setTimeout(() => { win.focus(); win.print(); }, 350);
      });
    });
  };

  const groups = docs.reduce((acc, d) => {
    acc[d.category] = acc[d.category] || [];
    acc[d.category].push(d);
    return acc;
  }, {});

  const CategoryIcon = ({ cat, ...p }) => {
    if (cat === 'Marketing') return <Megaphone {...p} />;
    if (cat === 'Training') return <FilmStrip {...p} />;
    if (cat === 'Universal Migrator') return <Scales {...p} />;
    return <BookOpen {...p} />;
  };

  if (loading) {
    return <div className="p-8 text-[13px] text-[#666]" data-testid="kit-loading">Loading your kit...</div>;
  }
  if (content === '__forbidden__') {
    return (
      <div className="p-8 max-w-xl mx-auto" data-testid="kit-forbidden">
        <h1 className="text-2xl font-bold text-[#111] mb-2">Access restricted</h1>
        <p className="text-[13px] text-[#555]">The Marketing & Training Kit is visible to <strong>admin</strong> and <strong>manager</strong> roles. If you should have access, contact your workspace owner.</p>
      </div>
    );
  }

  // Sarah dataset CSVs live outside the markdown library — served by
  // /api/marketing/sample-dataset-sarah/{filename} as a public download.
  // Show download buttons in the toolbar when this entry is open so
  // Kory / Grayson can grab the actual demo file, not just the README.
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const isSarahDataset = current?.id === 'mk-sarah-dataset';
  const isCapterraShots = current?.id === 'mk-capterra-screenshots';
  const CAPTERRA_SHOTS = [
    ['capterra-01-contact-card.png', 'Contact Card'],
    ['capterra-02-contact-list.png', 'Contact List'],
    ['capterra-03-calendar.png', 'Calendar'],
    ['capterra-04-pipeline.png', 'Pipeline'],
    ['capterra-05-reports.png', 'Reports'],
  ];
  const isCapterraVideos = current?.id === 'mk-capterra-videos';
  const CAPTERRA_VIDEOS = [
    ['videocrunch-demo-narrated.mp4', '1. Main Demo'],
    ['migrationcrunch-demo.mp4', '2. Migration'],
    ['videocrunch-crunchy-bulk-email.mp4', '3. Crunchy AI'],
    ['videocrunch-lookup-smartgroup.mp4', '4. Lookup'],
  ];
  const isVcxListing = current?.id === 'mk-videocrunch-listing';
  const VCX_SHOTS = [
    ['videocrunch-01-landing.png', 'Landing Hero'],
    ['videocrunch-02-pricing.png', 'Pricing'],
    ['videocrunch-03-dashboard.png', 'Customer Dashboard'],
    ['videocrunch-04-voice-corrector.png', 'Voice Corrector'],
    ['videocrunch-05-admin-queue.png', 'Production Queue'],
  ];

  return (
    <div className="flex h-full bg-[#E8E8E8]" data-testid="marketing-kit-page">
      {/* Left Nav */}
      <aside className="w-72 bg-white border-r border-[#CCC] overflow-y-auto shrink-0">
        <div className="bg-gradient-to-b from-[#F39C12] to-[#D68910] px-4 py-2 border-b border-[#999] sticky top-0 z-10">
          <h1 className="text-[14px] font-bold text-white flex items-center gap-2">
            <ShareNetwork size={16} weight="bold" />Marketing & Training Kit
          </h1>
        </div>
        <div className="p-3 border-b border-[#EEE] bg-[#FFFBEB]">
          <p className="text-[11px] text-[#555] leading-relaxed">
            Ready-to-use marketing copy and video scripts. <strong>Click a doc</strong> to read it, then <strong>Copy</strong> or <strong>Download</strong> to paste anywhere — LinkedIn, email, a script for your narrator, etc.
          </p>
        </div>

        {Object.keys(groups).map(cat => (
          <div key={cat}>
            <div className="px-3 py-2 bg-[#F5F5F5] text-[11px] font-bold text-[#333] uppercase tracking-wide flex items-center gap-1.5 border-b border-[#EEE]">
              <CategoryIcon cat={cat} size={12} weight="bold" className="text-[#0B5394]" />
              {cat}
            </div>
            {groups[cat].map(d => {
              const active = current?.id === d.id;
              return (
                <button
                  key={d.id}
                  onClick={() => openDoc(d.id)}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 text-[12px] border-l-4 transition-colors ${
                    active ? 'border-[#F39C12] bg-[#FFF8EC] text-[#0B5394] font-semibold' : 'border-transparent hover:bg-[#F5F5F5] text-[#333]'
                  }`}
                  data-testid={`kit-doc-${d.id}`}
                >
                  <span className="flex-1 truncate">{d.title}</span>
                  <CaretRight size={10} weight="bold" className="text-[#999]" />
                </button>
              );
            })}
          </div>
        ))}
      </aside>

      {/* Right Content */}
      <main className="flex-1 overflow-y-auto" data-testid="kit-content">
        {current && (
          <>
            <div className="sticky top-0 z-10 bg-white border-b border-[#CCC] px-6 py-3 flex items-center justify-between shadow-sm">
              <div>
                <div className="text-[10px] text-[#777] uppercase tracking-wide">{current.category}</div>
                <div className="text-[15px] font-bold text-[#111]">{current.title}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={copyAll}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-sm border transition-colors ${
                    copied ? 'bg-green-50 border-green-400 text-green-700' : 'border-[#B0B0B0] hover:bg-[#F5F5F5] text-[#333]'
                  }`}
                  data-testid="kit-copy-btn"
                >
                  {copied ? <><Check size={13} weight="bold" />Copied!</> : <><Copy size={13} weight="bold" />Copy All</>}
                </button>
                <button
                  onClick={download}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium border border-[#B0B0B0] hover:bg-[#F5F5F5] text-[#333] rounded-sm"
                  data-testid="kit-download-btn"
                  title="Download original markdown source"
                >
                  <Download size={13} weight="bold" />.md
                </button>
                <button
                  onClick={downloadPdf}
                  disabled={pdfBusy}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium bg-[#0B5394] text-white hover:bg-[#073763] disabled:opacity-50 rounded-sm"
                  data-testid="kit-pdf-btn"
                  title="Download styled PDF"
                >
                  <Download size={13} weight="bold" />{pdfBusy ? 'Building PDF...' : 'PDF'}
                </button>
                <button
                  onClick={printDoc}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium border border-[#B0B0B0] hover:bg-[#F5F5F5] text-[#333] rounded-sm"
                  data-testid="kit-print-btn"
                  title="Open in a print-friendly window (Ctrl+P → Save as PDF)"
                >
                  🖨 Print
                </button>
              </div>
            </div>

            {/* Sarah dataset — inline CSV download strip so Kory/Grayson can
                grab the actual demo file (README alone isn't enough). */}
            {isSarahDataset && (
              <div className="bg-emerald-50 border-b border-emerald-200 px-6 py-3 flex flex-wrap items-center gap-3" data-testid="kit-sarah-downloads">
                <div className="text-[12px] font-bold text-emerald-800">
                  📊 Demo CSV files:
                </div>
                <a
                  href={`${API_BASE}/api/marketing/sample-dataset-sarah/greenleaf-before.csv`}
                  download
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm"
                  data-testid="kit-dl-greenleaf-before"
                >
                  <Download size={13} weight="bold" />greenleaf-before.csv (2.4 MB)
                </a>
                <a
                  href={`${API_BASE}/api/marketing/sample-dataset-sarah/greenleaf-after.csv`}
                  download
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm"
                  data-testid="kit-dl-greenleaf-after"
                >
                  <Download size={13} weight="bold" />greenleaf-after.csv (3.1 MB)
                </a>
                <div className="text-[11px] text-emerald-700 ml-auto">
                  Public links — anyone can download.
                </div>
              </div>
            )}

            {/* Capterra screenshots — one-click PNG downloads. Files are
                static in /public/screenshots, served same-origin. */}
            {isCapterraShots && (
              <div className="bg-emerald-50 border-b border-emerald-200 px-6 py-3 flex flex-wrap items-center gap-2" data-testid="kit-capterra-downloads">
                <div className="text-[12px] font-bold text-emerald-800 mr-1">
                  📸 Download PNGs:
                </div>
                {CAPTERRA_SHOTS.map(([file, label], i) => (
                  <a
                    key={file}
                    href={`/screenshots/${file}`}
                    download={file}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm"
                    data-testid={`kit-dl-capterra-${i + 1}`}
                  >
                    <Download size={13} weight="bold" />{i + 1}. {label}
                  </a>
                ))}
                <div className="text-[11px] text-emerald-700 ml-auto">
                  Captions for each are in the doc below.
                </div>
              </div>
            )}

            {/* Capterra videos — one-click MP4 downloads for YouTube upload. */}
            {isCapterraVideos && (
              <div className="bg-emerald-50 border-b border-emerald-200 px-6 py-3 flex flex-wrap items-center gap-2" data-testid="kit-capterra-video-downloads">
                <div className="text-[12px] font-bold text-emerald-800 mr-1">
                  🎬 Download MP4s:
                </div>
                {CAPTERRA_VIDEOS.map(([file, label], i) => (
                  <a
                    key={file}
                    href={`/${file}`}
                    download={file}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm"
                    data-testid={`kit-dl-video-${i + 1}`}
                  >
                    <Download size={13} weight="bold" />{label}
                  </a>
                ))}
                <div className="text-[11px] text-emerald-700 ml-auto">
                  Then upload to YouTube — steps below.
                </div>
              </div>
            )}

            {/* VideoCrunch listing screenshots — one-click PNG downloads. */}
            {isVcxListing && (
              <div className="bg-emerald-50 border-b border-emerald-200 px-6 py-3 flex flex-wrap items-center gap-2" data-testid="kit-videocrunch-downloads">
                <div className="text-[12px] font-bold text-emerald-800 mr-1">
                  📸 Download PNGs:
                </div>
                {VCX_SHOTS.map(([file, label], i) => (
                  <a
                    key={file}
                    href={`/screenshots/${file}`}
                    download={file}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm"
                    data-testid={`kit-dl-vcx-${i + 1}`}
                  >
                    <Download size={13} weight="bold" />{i + 1}. {label}
                  </a>
                ))}
                <a
                  href="/videocrunch-presenter-test.mp4"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-bold bg-[#F39C12] text-white hover:bg-[#d9880c] rounded-sm"
                  data-testid="kit-presenter-test-link"
                >
                  ▶ AI Presenter Test (36s)
                </a>
                <div className="text-[11px] text-emerald-700 ml-auto">
                  Captions for each are in the doc below.
                </div>
              </div>
            )}

            {/* Universal Migrator kit — one-click links to every asset,
                shown on all docs in that category. */}
            {current?.id?.startsWith('um-') && (
              <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex flex-wrap items-center gap-2" data-testid="kit-um-links">
                <div className="text-[12px] font-bold text-amber-800 mr-1">
                  ⚖️ NoteCrunch assets:
                </div>
                <a
                  href="/notecrunch"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-bold bg-[#C9A44C] text-black hover:bg-[#B8933B] rounded-sm"
                  data-testid="kit-um-demo-link"
                >
                  ⚖ Demo page (/notecrunch)
                </a>
                <a
                  href="/videos/notecrunch-legal-demo.mp4"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-bold bg-[#0B5394] text-white hover:bg-[#073763] rounded-sm"
                  data-testid="kit-um-video-link"
                >
                  ▶ Demo video (95s)
                </a>
                <a
                  href="/licensing/notecrunch-terms-sheet.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm"
                  data-testid="kit-um-terms-link"
                >
                  📄 Terms sheet
                </a>
                <div className="text-[11px] text-amber-700 ml-auto">
                  Links open on this site — same URLs work on salescrunchcrm.com.
                </div>
              </div>
            )}

            <article ref={contentRef} className="max-w-3xl mx-auto px-8 py-8 bg-white my-6 border border-[#CCC] shadow-sm prose prose-slate max-w-none"
                     data-testid="kit-article">
              <div className="markdown-body">
                <ReactMarkdown>{content}</ReactMarkdown>
              </div>
            </article>
          </>
        )}
      </main>
    </div>
  );
}
